"use client";

import React from "react";
import { Footer } from "@/components/LandingLayout";
import { 
  Zap, 
  Layers, 
  PlayCircle, 
  Database, 
  BarChart3,
  Search,
  Cpu,
  Globe,
  Lock
} from "lucide-react";

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 font-sans antialiased transition-colors duration-300">

      <main className="container mx-auto px-4 max-w-5xl pt-20 pb-20">
        <header className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-600/10 text-emerald-600 dark:text-emerald-500 text-[10px] font-black uppercase tracking-widest mb-6">
            <Cpu className="w-3 h-3" />
            Core Capabilities
          </div>
          <h1 className="text-3xl lg:text-5xl font-black tracking-tight text-zinc-900 dark:text-white mb-4">Industrial Extraction Engine.</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 max-w-xl mx-auto leading-relaxed">
            A single endpoint for all your TeraBox needs. High-availability and sub-second performance guaranteed.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-8 mb-24">
          {[
            {
              title: "Smart Link Extraction",
              desc: "Our engine handles all TeraBox mirror domains (terabox.com, 1024terabox.com, teraboxapp.com) and automatically resolves shortlinks and redirects to the direct source.",
              icon: <Search className="w-5 h-5 text-blue-500" />,
              accent: "bg-blue-500/5 dark:bg-blue-500/10"
            },
            {
              title: "Built-in Stream URLs",
              desc: "Every extraction response includes direct stream URLs for video and audio content. No separate conversion needed—just grab the link and play it in your player.",
              icon: <PlayCircle className="w-5 h-5 text-emerald-500" />,
              accent: "bg-emerald-500/5 dark:bg-emerald-500/10"
            },
            {
              title: "Recursive Folder Support",
              desc: "Browse directory trees with ease. Our API returns structured folder data, allowing you to build full-featured file explorers within your application.",
              icon: <Layers className="w-5 h-5 text-purple-500" />,
              accent: "bg-purple-500/5 dark:bg-purple-500/10"
            },
            {
              title: "Real-time ZIP Generation",
              desc: "Enable folder downloads without server-side storage. Our ZIP engine streams compressed data directly from TeraBox servers to your users in real-time.",
              icon: <Database className="w-5 h-5 text-orange-500" />,
              accent: "bg-orange-500/5 dark:bg-orange-500/10"
            },
            {
              title: "Simple API Key Auth",
              desc: "Fast and secure authentication using the X-API-KEY header. No complex HMAC signing or session management required for standard integrations.",
              icon: <Lock className="w-5 h-5 text-red-500" />,
              accent: "bg-red-500/5 dark:bg-red-500/10"
            },
            {
              title: "Sub-second Precision Analytics",
              desc: "Monitor your request volume, wallet balance, and success rates through our dedicated dashboard with real-time updates.",
              icon: <BarChart3 className="w-5 h-5 text-cyan-500" />,
              accent: "bg-cyan-500/5 dark:bg-cyan-500/10"
            }
          ].map((feature, i) => (
            <div key={i} className="p-8 bg-zinc-50 dark:bg-zinc-900/30 border border-zinc-200 dark:border-zinc-800 rounded-3xl group hover:border-blue-600/30 dark:hover:border-zinc-700 transition-all shadow-sm dark:shadow-none">
              <div className={`w-12 h-12 rounded-2xl ${feature.accent} flex items-center justify-center mb-6 border border-zinc-200 dark:border-white/5 group-hover:scale-110 transition-transform`}>
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold mb-3 text-zinc-900 dark:text-white">{feature.title}</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Technical Stack */}
        <section className="bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 p-12 rounded-[3rem] text-center shadow-sm dark:shadow-none">
          <Globe className="w-8 h-8 text-blue-500 mx-auto mb-6 opacity-50" />
          <h2 className="text-2xl font-bold mb-4 text-zinc-900 dark:text-white">Reactive Infrastructure.</h2>
          <div className="flex flex-wrap justify-center gap-8 mt-10">
            <div className="flex flex-col items-center">
              <div className="text-xl font-black text-zinc-900 dark:text-white">85ms</div>
              <div className="text-[10px] font-bold text-zinc-400 dark:text-zinc-600 uppercase tracking-widest mt-1">Latency</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-xl font-black text-zinc-900 dark:text-white">12/s</div>
              <div className="text-[10px] font-bold text-zinc-400 dark:text-zinc-600 uppercase tracking-widest mt-1">Req/Sec</div>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-xl font-black text-zinc-900 dark:text-white">99.9%</div>
              <div className="text-[10px] font-bold text-zinc-400 dark:text-zinc-600 uppercase tracking-widest mt-1">Success</div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
